import React, { useState, useEffect } from 'react';
import { View, FlatList, StyleSheet, TouchableOpacity, Alert, ActivityIndicator } from 'react-native';
import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../theme/ThemeContext';
import MemberCard from '../../components/clubs/MemberCard';
import { ClubsStackParamList } from '../../types/navigation';
import ThemedText from '../../components/common/ThemedText';
import { getClubById, Club, ClubMember } from '../../lib/api/clubs';
import { useAuth } from '../../context/AuthContext';

type ClubMembersRouteProp = RouteProp<ClubsStackParamList, 'ClubMembers'>;

const ClubMembersScreen: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigation = useNavigation();
  const route = useRoute<ClubMembersRouteProp>();
  const { clubId } = route.params;

  const [club, setClub] = useState<Club | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClubData();
  }, [clubId]);

  const loadClubData = async () => {
    try {
      setLoading(true);
      const data = await getClubById(clubId);
      setClub(data);
    } catch (error) {
      console.error('Error loading club:', error);
      Alert.alert('Error', 'Failed to load club members');
    } finally {
      setLoading(false);
    }
  };

  const members = club?.members || [];
  const isAdmin = club?.isAdmin || user?.role === 'admin';

  const handleBack = () => {
    navigation.goBack();
  };

  const handleMemberPress = (memberId: string) => {
    // In a real app, this would navigate to the member's profile
    Alert.alert('View Profile', `Viewing profile for member ${memberId}`);
  };

  const handleRemoveMember = (memberId: string, memberName: string) => {
    Alert.alert(
      'Remove Member',
      `Are you sure you want to remove ${memberName} from the club?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            Alert.alert('Success', `${memberName} has been removed from the club`);
          },
        },
      ]
    );
  };

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Ionicons name="people-outline" size={48} color={theme.textTertiary} />
      <ThemedText style={[styles.emptyStateText, { color: theme.textTertiary }]}>No members yet</ThemedText>
    </View>
  );

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={[styles.header, { backgroundColor: theme.card, borderBottomColor: theme.border }]}>
          <TouchableOpacity onPress={handleBack} style={styles.headerButton}>
            <Ionicons name="arrow-back" size={28} color={theme.text} />
          </TouchableOpacity>
          <View style={styles.headerInfo}>
            <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Members</ThemedText>
          </View>
          <View style={styles.headerButton} />
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View style={[styles.header, { backgroundColor: theme.card, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={handleBack} style={styles.headerButton}>
          <Ionicons name="arrow-back" size={28} color={theme.text} />
        </TouchableOpacity>
        <View style={styles.headerInfo}>
          <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Members</ThemedText>
          <ThemedText style={[styles.headerSubtitle, { color: theme.textSecondary }]}>{members.length} members</ThemedText>
        </View>
        <View style={styles.headerButton} />
      </View>

      <FlatList
        data={members}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <MemberCard
            member={{
              id: item.userId,
              name: item.user.name,
              profession: item.user.profession || 'Member',
              city: '', // API doesn't provide city in member data
              role: 'member', // We'd need to check if userId matches admin
            }}
            onPress={() => handleMemberPress(item.userId)}
            showRemoveButton={isAdmin}
            onRemovePress={() => handleRemoveMember(item.userId, item.user.name)}
          />
        )}
        contentContainerStyle={[
          styles.listContent,
          members.length === 0 && styles.listContentEmpty,
        ]}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={renderEmptyState}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  headerSubtitle: {
    fontSize: 12,
    marginTop: 2,
  },
  listContent: {
    paddingTop: 12,
    paddingBottom: 20,
  },
  listContentEmpty: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateText: {
    fontSize: 16,
    marginTop: 12,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ClubMembersScreen;
